import { nanoid } from "nanoid";
import {
  deepCopy,
  getThread as getThreadQuery,
  getTerminalState as getTerminalStateQuery,
  getThreadSequence as getThreadSequenceQuery,
  getConnections as getConnectionsQuery,
  getThreadByState as getThreadByStateQuery,
} from "./radUtils.js";
import { getTargetConnections as getTargetConnectionsQuery } from "./radTargets.js";

import { insertAtIndex } from "@cadenza/utils";

// NB. Unexported functions throw errors for invalid inputs
// and rely on callers to handle exceptions.

// Result wrapper for consistent success/error handling
// in exported functions.
const createResult = (success, data, message) => ({
  success,
  data,
  message,
  error: success ? null : message,
});

const STATE_TEMPLATE = { class: "state", type: "state" };
const DEFAULT_ROLE_POSITION = { x: 20, y: 20 };
const DEFAULT_LABEL_POSITION = { x: 20, y: 0 };
const DEFAULT_THREAD_POSITION = { x: 20, y: 20 };
const DEFAULT_REF_LABEL_POSITION = { x: 0, y: -10 };

const ensureArray = (value) => (Array.isArray(value) ? value : []);

const clonePoint = (point, fallback = { x: 0, y: 0 }) => {
  if (!point || typeof point !== "object") return { ...fallback };
  const x = Number(point.x);
  const y = Number(point.y);
  return {
    x: Number.isFinite(x) ? x : fallback.x,
    y: Number.isFinite(y) ? y : fallback.y,
  };
};

const cloneSize = (size, fallback = { width: 0, height: 0 }) => {
  if (!size || typeof size !== "object") return { ...fallback };
  const width = Number(size.width);
  const height = Number(size.height);
  return {
    width: Number.isFinite(width) ? width : fallback.width,
    height: Number.isFinite(height) ? height : fallback.height,
  };
};

const prepareRad = (rad, bumpAmount = 0) => {
  const next = deepCopy(rad ?? {});
  next.version = typeof next.version === "number" ? next.version : 0;
  if (bumpAmount > 0) {
    next.version += bumpAmount;
  }
  next.info =
    typeof next.info === "object" && next.info !== null ? next.info : {};
  next.roles = ensureArray(next.roles);
  next.threads = ensureArray(next.threads);
  next.activities = ensureArray(next.activities);
  next.states = ensureArray(next.states);
  next.connections = ensureArray(next.connections);
  return next;
};

const ensureState = (rad, stateId, label) => {
  if (!stateId) return;
  const exists = rad.states.some((state) => state.id === stateId);
  if (!exists) {
    rad.states.push({
      ...STATE_TEMPLATE,
      id: stateId,
      label: label ?? "",
      show: false,
    });
  }
};

const ensureUniqueInteractions = (interactions = []) => {
  const seen = new Set();
  return interactions.filter((id) => {
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

const pruneConnectionsForActivity = (rad, activityId) => {
  if (!activityId) return;

  rad.connections = rad.connections
    .map((connection) => ({
      ...connection,
      interactions: connection.interactions.filter((id) => id !== activityId),
    }))
    .filter((connection) => connection.interactions.length >= 2);
};

const removeStateIfUnused = (rad, stateId) => {
  if (!stateId) return;
  const stillUsed = rad.activities.some(
    (activity) =>
      activity.prestate_id === stateId || activity.poststate_id === stateId
  );
  if (stillUsed) return;
  const referencedByThread = rad.threads.some(
    (thread) =>
      thread.prestate_id === stateId || thread.poststate_id === stateId
  );
  if (referencedByThread) return;
  rad.states = rad.states.filter((state) => state.id !== stateId);
};

const removeThreadTree = (rad, threadId) => {
  if (!threadId) return;
  console.log("Removing thread tree for thread:", threadId);
  const threadRecord = rad.threads.find((thread) => thread.id === threadId);
  const activities = rad.activities.filter((a) => a.thread_id === threadId);
  activities.forEach((activity) => {
    if (activity.class === "refinement") {
      const childThreads = rad.threads.filter(
        (thread) => thread.parent_id === activity.id
      );
      childThreads.forEach((child) => removeThreadTree(rad, child.id));
    }
    if (activity.type === "interaction") {
      pruneConnectionsForActivity(rad, activity.id);
    }
    removeStateIfUnused(rad, activity.prestate_id);
    removeStateIfUnused(rad, activity.poststate_id);
    rad.activities = rad.activities.filter((a) => a.id !== activity.id);
  });
  rad.threads = rad.threads.filter((thread) => thread.id !== threadId);
  if (threadRecord) {
    removeStateIfUnused(rad, threadRecord.prestate_id);
    removeStateIfUnused(rad, threadRecord.poststate_id);
  }
};

const deleteActivityById = (rad, activityId) => {
  // Delete an activity by its ID.
  // If the parent thread becomes empty, delete the thread as well.
  // Otherwise, link the preceding and following activities' states.
  // Reurn the parent thread for possible further processing.

  const activity = rad.activities.find((a) => a.id === activityId);
  if (!activity) {
    return;
  }
  const thread = getThreadQuery(rad)(activity.thread_id);
  if (!thread) {
    console.warn("Thread not found for activity:", activityId);
    return;
  }
  const threadActivities = getThreadSequenceQuery(rad)(
    thread.id,
    thread.prestate_id
  );

  const activityIndex = threadActivities.findIndex((a) => a.id === activityId);
  if (activityIndex === -1) {
    return;
  }
  const precedingActivity = threadActivities[activityIndex - 1];
  const followingActivity = threadActivities[activityIndex + 1];
  console.log(
    "Deleting activity:",
    activity,
    precedingActivity,
    followingActivity
  );
  // If there is a preceding and following activity, link their states
  if (precedingActivity && followingActivity) {
    precedingActivity.poststate_id = followingActivity.prestate_id;
  } else if (precedingActivity && !followingActivity) {
    // If only preceding activity exists, set its poststate to thread's poststate
    precedingActivity.poststate_id = thread.poststate_id;
  } else if (!precedingActivity && followingActivity) {
    // If only following activity exists, set its prestate to thread's prestate
    followingActivity.prestate_id = thread.prestate_id;
  }
  rad.activities = rad.activities.filter(
    (activity) => activity.id !== activityId
  );
  if (activity) {
    removeStateIfUnused(rad, activity.prestate_id);
    removeStateIfUnused(rad, activity.poststate_id);
  }

  return thread;
};

const deleteElementById = (rad, id, className, type) => {
  if (!id || !className) {
    throw new Error("Invalid id or className");
  }
  const next = prepareRad(rad, 1);

  const removeRole = (roleId) => {
    const roleThreads = next.threads.filter(
      (thread) => thread.role_id === roleId
    );
    roleThreads.forEach((thread) => removeThreadTree(next, thread.id));
    next.roles = next.roles.filter((role) => role.id !== roleId);
  };

  switch (className) {
    case "role": {
      removeRole(id);
      break;
    }
    case "thread": {
      removeThreadTree(next, id);
      break;
    }
    case "case":
    case "part": {
      // Check the parent to ensure > 2 subthreads
      const parentRefinement = next.activities.find(
        (activity) =>
          activity.id === next.threads.find((t) => t.id === id)?.parent_id
      );
      if (parentRefinement) {
        const subthreads = next.threads.filter(
          (thread) => thread.parent_id === parentRefinement.id
        );
        if (subthreads.length > 2) {
          removeThreadTree(next, id);
        } else {
          // raise error
          throw new Error(
            "Cannot delete: 'case' and 'part' refinements must have at least two subthreads."
          );
        }
      } else {
        // raise error
        throw new Error("Parent refinement is missing!!!");
      }
      break;
    }
    case "repeat": {
      throw new Error(
        "Cannot delete: 'repeat' refinements must have at least one subthread."
      );
    }
    case "refinement": {
      const subthreads = next.threads.filter(
        (thread) => thread.parent_id === id
      );
      subthreads.forEach((thread) => removeThreadTree(next, thread.id));
      if (type === "interaction") {
        pruneConnectionsForActivity(next, id);
      }
      next.activities = next.activities.filter(
        (activity) => activity.id !== id
      );
      break;
    }
    case "activity": {
      console.log("Deleting activity by id:", id);
      const thread = deleteActivityById(next, id);
      // If the thread has no more activities, remove the thread
      const remainingActivities = next.activities.filter(
        (a) => a.thread_id === thread.id
      );
      // Main threads only get deleted if empty. Subthreads are left alone.
      if (thread.class === "thread" && remainingActivities.length === 0) {
        console.log("Removing thread tree for thread activity:", thread.id);
        removeThreadTree(next, thread.id);
      } else if (
        thread.class !== "thread" &&
        remainingActivities.length === 1
      ) {
        // For subthreads, if only the placeholder activity remains, remove it
        const remainingActivity = remainingActivities[0];
        if (remainingActivity.type === "void") {
          deleteActivityById(next, remainingActivity.id);
        }
      }
      break;
    }
    case "connection": {
      next.connections = next.connections.filter(
        (connection) => connection.id !== id
      );
      break;
    }
    default:
      break;
  }

  return next;
};

// Wrapper to ensure appendRefinementThreads is called after activity addition
const withRefinementThreads = (fn) => {
  return (rad, payload) => {
    const originalActivities = (rad.activities || []).slice();
    const result = fn(rad, payload);
    const resultActivities = result.activities || [];

    // Find newly added activities and call appendRefinementThreads for each
    const newActivities = resultActivities.filter(
      (activity) => !originalActivities.some((orig) => orig.id === activity.id)
    );

    newActivities.forEach((newActivity) => {
      appendRefinementThreads(result, newActivity);
    });

    return result;
  };
};

const appendRefinementThreads = (rad, activity) => {
  if (!activity || activity.class !== "refinement") return;
  console.log("APPEND REFINEMENT THREADS: ", activity);
  let newThreads = [];
  switch (activity.type) {
    case "case": {
      newThreads = [
        {
          id: nanoid(),
          class: "case",
          type: "condition",
          label: "c1",
          parent_id: activity.id,
          prestate_id: null, //activity.prestate_id,
          poststate_id: null, //activity.poststate_id,
          index: 0,
          status: "main",
        },
        {
          id: nanoid(),
          class: "case",
          type: "condition",
          label: "c2",
          parent_id: activity.id,
          prestate_id: null, // activity.prestate_id,
          poststate_id: null, // activity.poststate_id,
          index: 1,
          status: "main",
        },
      ];
      break;
    }
    case "part": {
      newThreads = [
        {
          id: nanoid(),
          class: "part",
          type: "partThread",
          label: "p1",
          parent_id: activity.id,
          prestate_id: null, // activity.prestate_id,
          poststate_id: null, // activity.poststate_id,
          index: 0,
        },
        {
          id: nanoid(),
          class: "part",
          type: "partThread",
          label: "p2",
          parent_id: activity.id,
          prestate_id: null, // activity.prestate_id,
          poststate_id: null, // activity.poststate_id,
          index: 1,
        },
      ];
      break;
    }
    case "repeat": {
      newThreads = [
        {
          id: nanoid(),
          class: "repeat",
          type: "repeat",
          label: "r1",
          parent_id: activity.id,
          prestate_id: null, // activity.prestate_id,
          poststate_id: null, // activity.poststate_id,
          index: 0,
        },
      ];
      break;
    }
    default:
      break;
  }
  if (newThreads.length) {
    rad.threads = rad.threads.concat(newThreads);
  }
};

const pickExtras = (payload, exclude) => {
  const extra = {};
  Object.entries(payload || {}).forEach(([key, value]) => {
    if (!exclude.includes(key)) {
      extra[key] = value;
    }
  });
  return extra;
};

export const replaceRad = (_rad, nextRad) => deepCopy(nextRad ?? {});

export const clearRad = () => ({});

export const updateInfo = (rad, info) => {
  if (!info || typeof info !== "object") {
    return createResult(false, null, "Invalid info object");
  }
  const next = prepareRad(rad, 1);
  next.info = { ...next.info, ...info };
  return createResult(true, next, "Successfully updated RAD info");
};

export const addRole = (
  rad,
  {
    id,
    label = "New Role",
    position,
    size,
    transient = true,
    multiple = false,
  } = {}
) => {
  if (
    !position ||
    typeof position.x !== "number" ||
    typeof position.y !== "number"
  ) {
    return createResult(false, null, "Valid position coordinates are required");
  }

  const next = prepareRad(rad, 1);
  const roleId = id ?? nanoid();

  if (next.roles.some((role) => role.id === roleId)) {
    return createResult(false, null, `Role ${roleId} already exists`);
  }

  next.roles.push({
    id: roleId,
    class: "role",
    label,
    position: clonePoint(position, DEFAULT_THREAD_POSITION),
    size: cloneSize(size, { width: 200, height: 320 }),
    transient: !!transient,
    multiple: !!multiple,
  });

  return createResult(true, next, `Successfully added role "${label}"`);
};

export const updateRole = (rad, { id, ...patch } = {}) => {
  if (!id) {
    return createResult(false, null, "Role id required");
  }
  const next = prepareRad(rad, 1);
  const idx = next.roles.findIndex((role) => role.id === id);
  if (idx === -1) {
    return createResult(false, null, `Role ${id} not found`);
  }
  const role = next.roles[idx];
  next.roles[idx] = {
    ...role,
    ...patch,
    position: patch.position
      ? clonePoint(patch.position, role.position)
      : role.position,
    size: patch.size ? cloneSize(patch.size, role.size) : role.size,
  };
  return createResult(true, next, `Successfully updated role "${role.label}"`);
};

export const deleteRole = (rad, roleId) => {
  if (!roleId) return rad;
  const next = deleteElementById(rad, roleId, "role");
  return next;
};

export const updateThread = (rad, { id, ...patch } = {}) => {
  if (!id) {
    return createResult(false, null, "Thread id required");
  }
  const next = prepareRad(rad, 1);
  const idx = next.threads.findIndex((thread) => thread.id === id);
  if (idx === -1) {
    return createResult(false, null, `Thread ${id} not found`);
  }
  const thread = next.threads[idx];
  next.threads[idx] = {
    ...thread,
    ...patch,
    position: patch.position
      ? clonePoint(patch.position, thread.position || DEFAULT_THREAD_POSITION)
      : thread.position,
  };
  return createResult(
    true,
    next,
    `Successfully updated thread "${thread.label || thread.id}"`
  );
};

export const addActivity = (rad, { activity, target } = {}) => {
  try {
    const {
      id: targetId,
      classname: targetClass,
      type: targetType,
    } = target || {};
    if (!targetId || !targetClass || !targetType) {
      return createResult(
        false,
        null,
        "Target id, class, and type are required"
      );
    }

    const {
      classname: activityClass,
      type: activityType,
      ...rest
    } = activity || {};
    if (!activityClass || !activityType) {
      return createResult(false, null, "Activity class and type are required");
    }

    if (
      !(
        ["role", "thread", "state", "activity"].includes(targetClass) ||
        ["condition", "partThread", "repeat"].includes(targetType)
      )
    ) {
      return createResult(
        false,
        null,
        `Adding activity to target class ${targetClass} not yet supported`
      );
    }

    let next;
    let successMessage;

    if (targetClass === "role") {
      next = withRefinementThreads(addActivityToRole)(rad, {
        role_id: targetId,
        className: activityClass,
        type: activityType,
        ...rest,
      });
      successMessage = `Successfully added ${activityType} activity to role`;
    } else if (targetClass === "thread") {
      next = withRefinementThreads(addActivityToThread)(rad, {
        thread_id: targetId,
        className: activityClass,
        type: activityType,
        ...rest,
      });
      successMessage = `Successfully added ${activityType} activity to thread`;
    } else if (["condition", "partThread", "repeat"].includes(targetType)) {
      next = withRefinementThreads(addActivityToSubthread)(rad, {
        thread_id: targetId,
        className: activityClass,
        type: activityType,
        ...rest,
      });
      successMessage = `Successfully added ${activityType} activity to ${targetType}`;
    } else if (targetClass === "state") {
      next = withRefinementThreads(addActivityToState)(rad, {
        thread_id: target.parentThreadId,
        state_id: targetId,
        className: activityClass,
        type: activityType,
        ...rest,
      });
      successMessage = `Successfully added ${activityType} activity to state`;
    } else if (targetClass === "activity") {
      next = withRefinementThreads(addActivityToActivity)(rad, {
        activity_id: targetId,
        className: activityClass,
        type: activityType,
        ...rest,
      });
      successMessage = `Successfully added ${activityType} activity after existing activity`;
    }

    if (next) {
      return createResult(true, next, successMessage);
    }

    return createResult(
      false,
      null,
      `Adding activity to target class ${targetClass} not yet supported`
    );
  } catch (error) {
    return createResult(
      false,
      null,
      `Failed to add activity: ${error.message}`
    );
  }
};

const addActivityToRole = (
  rad,
  {
    role_id,
    className = "activity",
    type = "activity",
    label,
    labelPosition,
    position,
    id,
    prestate_id,
    poststate_id,
    ...rest
  } = {}
) => {
  console.log("ADD ACTIVITY TO ROLE: ", role_id, className, type);
  if (!role_id) throw new Error("role_id required");
  const next = prepareRad(rad, 1);
  const role = next.roles.find((r) => r.id === role_id);
  if (!role) throw new Error(`role ${role_id} not found`);
  const threadId = nanoid();
  const preId = prestate_id ?? nanoid();
  const postId = poststate_id ?? nanoid();
  const activityLabel = label ?? `New ${type}`;
  ensureState(next, preId, "START");
  ensureState(next, postId, "END");
  // The position here is for the thread, not the activity.
  // If it is relative to the originating editor , it must
  // be transformed to an offset from the parent role position.
  // If position is not supplied, use defaults. This latter case is
  // typical when auto adding interactions to a role, so we add
  // it near the bottom-right of the role in an attempt to
  // clear existing elements.
  const rolePosition = role.position || DEFAULT_ROLE_POSITION;
  const offsetPosition = position
    ? {
        x: position.x - rolePosition.x,
        y: position.y - rolePosition.y,
      }
    : { x: role.size.width - 50, y: role.size.height - 100 };

  console.log("OFFSET POSITION: ", offsetPosition);

  next.threads.push({
    id: threadId,
    role_id,
    class: "thread",
    label: rest.threadLabel,
    position: clonePoint(offsetPosition, DEFAULT_THREAD_POSITION),
    prestate_id: preId,
    poststate_id: postId,
  });
  const extra = pickExtras(rest, ["threadLabel"]);
  const newActivity = {
    id: id ?? nanoid(),
    class: className,
    type,
    label: activityLabel,
    labelPosition: clonePoint(labelPosition, DEFAULT_LABEL_POSITION),
    thread_id: threadId,
    prestate_id: preId,
    poststate_id: postId,
    ...extra,
  };

  next.activities.push(newActivity);
  return next;
};

const addActivityToState = (
  rad,
  { thread_id, state_id, className, type, ...rest } = {}
) => {
  console.log("ADD ACTIVITY TO STATE: ", thread_id, state_id, className, type);
  if (!state_id) throw new Error("state_id required");
  // The request is to add an activity to a state, which means
  // the nearest suitable place in the thread to which the state belongs.
  // Rules:
  // 1. If the state is the terminal state of the thread, add the activity
  //    as the last activity in the thread. Preserve the thread's poststate,
  //    i.e., create a new prestate for the new activity. The preceding activity's
  //    poststate becomes the new activity's prestate. The new activity's
  //    acquires the existing thread's poststate.
  // 2. If the state is not the terminal state, insert the activity
  //    prior to the activity whose prestate is the state. The new
  //    activity's poststate becomes the prestate of the existing activity.
  const next = prepareRad(rad, 1);
  const thread = getThreadQuery(next)(thread_id);
  if (!thread) throw new Error(`thread ${thread_id} not found`);
  const terminalState =
    getTerminalStateQuery(next)(thread_id) ?? thread.poststate_id;
  console.log("TERMINAL STATE: ", terminalState, state_id);
  if (state_id === terminalState) {
    // Rule 1
    console.log("ADD TO STATE RULE 1");
    const terminalActivity = next.activities.find(
      (activity) => activity.poststate_id === terminalState
    );
    if (!terminalActivity) throw new Error(`activity not found`);
    const newPrestateId = nanoid();
    const terminalActivityId = terminalActivity.id;

    ensureState(next, newPrestateId, `New ${type} Prestate`);
    // update the terminal activity to point to the new prestate
    const terminalIdx = next.activities.findIndex(
      (activity) => activity.id === terminalActivityId
    );
    if (terminalIdx === -1) throw new Error(`activity not found`);
    next.activities[terminalIdx] = {
      ...next.activities[terminalIdx],
      poststate_id: newPrestateId,
    };

    // add the new activity
    const extra = pickExtras(rest, []);
    const newActivity = {
      id: nanoid(),
      class: className,
      type: type,
      label: `New ${type}`,
      thread_id,
      prestate_id: newPrestateId,
      poststate_id: terminalState,
      ...extra,
    };
    next.activities.push(newActivity);

    return next;
  } else {
    // Rule 2
    console.log("ADD TO STATE RULE 2");
    const succeedingActivity = next.activities.find(
      (activity) => activity.prestate_id === state_id
    );
    if (!succeedingActivity) throw new Error(`activity not found`);

    const newPoststateId = nanoid();
    const succeedingActivityId = succeedingActivity.id;
    const originalPrestate = succeedingActivity.prestate_id; // This is state_id

    // copy the prestate for the new activity
    ensureState(next, newPoststateId, `New ${type} Poststate`);

    // update the succeeding activity
    const succeedingIdx = next.activities.findIndex(
      (activity) => activity.id === succeedingActivityId
    );
    if (succeedingIdx === -1) throw new Error(`activity not found`);
    next.activities[succeedingIdx].prestate_id = newPoststateId;
    // add the new activity
    const extra = pickExtras(rest, []);
    const newActivity = {
      id: nanoid(),
      class: className,
      type: type,
      label: `New ${type}`,
      thread_id,
      prestate_id: originalPrestate,
      poststate_id: newPoststateId,
      ...extra,
    };
    next.activities.push(newActivity);
    return next;
  }
};

const addActivityToActivity = (
  rad,
  { activity_id, className, type, ...rest } = {}
) => {
  console.log("ADD ACTIVITY TO ACTIVITY: ", activity_id, className, type);
  if (!activity_id) throw new Error("activity_id required");
  const next = prepareRad(rad, 1);
  const activity = next.activities.find((a) => a.id === activity_id);
  if (!activity) throw new Error(`activity ${activity_id} not found`);

  const newPrestateId = nanoid();
  const originalPoststate = activity.poststate_id;

  ensureState(next, newPrestateId, `New ${type} Prestate`);
  // update the existing activity poststate to point to the new prestate
  const activityIdx = next.activities.findIndex((a) => a.id === activity.id);
  if (activityIdx === -1) throw new Error(`activity ${activity.id} not found`);
  next.activities[activityIdx] = {
    ...next.activities[activityIdx],
    poststate_id: newPrestateId,
  };

  // add the new activity
  const extra = pickExtras(rest, []);
  const newActivity = {
    id: nanoid(),
    class: className ?? activity.class,
    type: type ?? "activity",
    label: `New ${type ?? "Activity"}`,
    thread_id: activity.thread_id,
    prestate_id: newPrestateId,
    poststate_id: originalPoststate,
    ...extra,
  };
  next.activities.push(newActivity);
  return next;
};

const addActivityToThread = (
  rad,
  { thread_id, className, type, ...rest } = {}
) => {
  // Adds an activity to the end of the specified thread.
  console.log("ADD ACTIVITY TO THREAD: ", thread_id, className, type);
  if (!thread_id) throw new Error("thread_id required");
  const next = prepareRad(rad, 1);
  const thread = getThreadQuery(next)(thread_id);
  if (!thread) throw new Error(`thread ${thread_id} not found`);
  const terminalState =
    getTerminalStateQuery(next)(thread_id) ?? thread.poststate_id;
  if (!terminalState) {
    throw new Error(`thread ${thread_id} has no terminal state`);
  }

  // Helper to create a new activity
  const createActivity = (prestate_id) => {
    const extra = pickExtras(rest, []);
    return {
      id: nanoid(),
      class: className,
      type: type,
      label: `New ${type}`,
      thread_id,
      prestate_id,
      poststate_id: terminalState,
      ...extra,
    };
  };

  // Helper to update terminal activity's poststate
  const updateTerminalActivity = ({ id, poststate_id }) => {
    const terminalIdx = next.activities.findIndex(
      (activity) => activity.id === id
    );
    if (terminalIdx === -1) throw new Error(`terminal activity not found`);
    next.activities[terminalIdx] = {
      ...next.activities[terminalIdx],
      poststate_id,
    };
  };

  const terminalActivity = next.activities.find(
    (activity) => activity.poststate_id === terminalState
  );
  if (!terminalActivity) throw new Error(`terminal activity not found`);

  const newPrestateId = nanoid();
  ensureState(next, newPrestateId, `New ${type} Prestate`);
  updateTerminalActivity({
    id: terminalActivity.id,
    poststate_id: newPrestateId,
  });
  next.activities.push(createActivity(newPrestateId));

  return next;
};

const addActivityToSubthread = (
  rad,
  { thread_id, className, type, ...rest } = {}
) => {
  // Add an activity to the start of the specified subthread.
  // Subthreads are refinement threads (classes case, part, repeat) and
  // treated a little differently from main threads.
  // The main differences are they:
  //  have a null prestate
  //  may or may not rejoin the parent activity's poststate.
  //     a thread that does not rejoin the parent activity's poststate
  //     has its own poststate.
  //     a thread that does rejoin the parent activity's poststate
  //     has a null poststate.
  //  may be empty (no activities), in which case prestate and poststate
  //  IDs are definitely null.
  // The rules for adding an activity to a subthread are:
  // 1. If the subthread is empty
  //    simply add the new activity with null prestate and poststate
  // 2. If the subthread is populated,
  //    create a new poststate for the new activity, update the next
  //    activity's prestate to point to the new poststate, and add the new activity.
  //    (Note that practically we only insert at the start of a subthread
  //    as in the graphical editor it would be difficult for the user to
  //    pinpoint the subthread's span; they can only select the symbol for the
  //    head of the thread.)
  // Because we always add to the start of a subthread, we needn't worry about
  // fiddling with terminal activities.
  // These operations still adhere to the bipartite graph structure of activities
  // and states.
  // Note that subthreads always have a parent activity, which is the refinement
  // activity that spawned them.
  // Thus we can always determine whether the subthread rejoins the parent
  // activity's poststate or has its own poststate.
  // It is noted that a subthread with a terminal state must have been disconnected
  // from the parent activity's poststate. Logically the poststate must point to
  // either the end state of the main thread or the end state of its nearest
  // ancestor thread or an earlier state (in case of loops).

  console.log("ADD ACTIVITY TO SUBTHREAD: ", thread_id, className, type);
  if (!thread_id) throw new Error("thread_id required");
  const next = prepareRad(rad, 1);
  const thread = getThreadQuery(next)(thread_id);
  if (!thread) throw new Error(`thread ${thread_id} not found`);

  // Helper to create a new activity
  const createActivity = (prestate_id, poststate_id) => {
    const extra = pickExtras(rest, []);
    return {
      id: nanoid(),
      class: className,
      type: type,
      label: `New ${type}`,
      thread_id,
      prestate_id: prestate_id,
      poststate_id: poststate_id,
      ...extra,
    };
  };

  const createVoidActivity = (poststate_id) => {
    return {
      id: nanoid(),
      class: "activity",
      type: "void",
      label: "null",
      thread_id,
      prestate_id: null,
      poststate_id: poststate_id,
    };
  };

  // Helper to update first activity's prestate
  const updateFirstActivity = ({ id, prestate_id }) => {
    const firstIdx = next.activities.findIndex(
      (activity) => activity.id === id
    );
    if (firstIdx === -1) throw new Error(`first activity not found`);
    next.activities[firstIdx] = {
      ...next.activities[firstIdx],
      prestate_id,
    };
  };

  // Refinement sub-thread
  const sequence = getThreadSequenceQuery(next)(thread_id);
  const isEmpty = sequence.length === 0;
  console.log("SUBTHREAD SEQUENCE: ", sequence, isEmpty);
  if (isEmpty) {
    console.log("SUBTHREAD IS EMPTY");
    // Empty refinement thread. Add null activity and the new activity
    // The null activity serves to anchor the prestate/poststate structure
    // and preserve strict bipartiteness. This enables in-thread loops
    // to be represented without special cases.
    const newPoststateId = nanoid();
    ensureState(next, newPoststateId, `${newPoststateId}`);
    next.activities.push(createVoidActivity(newPoststateId));
    next.activities.push(createActivity(newPoststateId, null));
  } else {
    console.log("SUBTHREAD IS POPULATED");
    // Populated refinement thread.
    // Add new activity before the first activity.
    // Create a new poststate for the new activity.
    const newPoststateId = nanoid();
    const nullActivity = sequence[0];
    ensureState(next, newPoststateId, `New ${type} Poststate`);
    //First (non-null) activity gets pushed down the line
    updateFirstActivity({ id: sequence[1].id, prestate_id: newPoststateId });
    next.activities.push(
      createActivity(nullActivity.poststate_id, newPoststateId)
    );
  }

  return next;
};

export const updateActivity = (rad, payload = {}) => {
  const { id } = payload;
  if (!id) {
    return createResult(false, null, "Activity id required");
  }
  const next = prepareRad(rad, 1);
  const idx = next.activities.findIndex((activity) => activity.id === id);
  if (idx === -1) {
    return createResult(false, null, `Activity ${id} not found`);
  }
  const existing = next.activities[idx];
  const patch = { ...payload };
  delete patch.id;
  if (patch.labelPosition) {
    patch.labelPosition = clonePoint(
      patch.labelPosition,
      existing.labelPosition ?? DEFAULT_LABEL_POSITION
    );
  }
  next.activities[idx] = { ...existing, ...patch };
  return createResult(
    true,
    next,
    `Successfully updated activity "${existing.label || existing.id}"`
  );
};

export const updateStateline = (rad, { id, label, show } = {}) => {
  if (!id || show === undefined) {
    return createResult(false, null, "State id and show property are required");
  }
  const next = prepareRad(rad, 1);
  const idx = next.states.findIndex((state) => state.id === id);
  if (idx === -1) {
    return createResult(false, null, `State ${id} not found`);
  }
  const state = next.states[idx];
  next.states[idx] = { ...state, show: !!show, label: label ?? state.label };
  return createResult(
    true,
    next,
    `Successfully updated state "${state.label || state.id}" visibility`
  );
};

export const updatePosition = (rad, { id, type, x, y } = {}) => {
  if (!id || !type) {
    return createResult(
      false,
      null,
      "Element id and type are required for position update"
    );
  }
  const next = prepareRad(rad, 1);

  // The only user modifiable positions are roles, threads, and labels
  switch (type) {
    case "role": {
      const position = clonePoint({ x, y });
      const idx = next.roles.findIndex((role) => role.id === id);
      if (idx === -1) {
        return createResult(false, null, `Role ${id} not found`);
      }
      next.roles[idx] = { ...next.roles[idx], position };
      break;
    }
    case "thread": {
      const position = clonePoint({ x, y });

      const idx = next.threads.findIndex((thread) => thread.id === id);
      if (idx === -1) {
        return createResult(false, null, `Thread ${id} not found`);
      }
      next.threads[idx] = { ...next.threads[idx], position };
      break;
    }
    case "label": {
      const position = clonePoint({ x, y }, DEFAULT_LABEL_POSITION);

      const idx = next.activities.findIndex((activity) => activity.id === id);
      if (idx === -1) {
        return createResult(false, null, `Activity ${id} not found`);
      }
      next.activities[idx] = {
        ...next.activities[idx],
        labelPosition: position,
      };
      break;
    }
    case "condition-label":
    case "repeat-label": {
      const position = clonePoint({ x, y }, DEFAULT_REF_LABEL_POSITION);

      console.log("UPDATE THREAD LABEL POSITION: ", id, position);
      const idx = next.threads.findIndex((thread) => thread.id === id);
      if (idx === -1) {
        return createResult(false, null, `Thread ${id} not found`);
      }
      next.threads[idx] = {
        ...next.threads[idx],
        labelPosition: position,
      };
      break;
    }
    default:
      return createResult(false, null, `Unknown position target type: ${type}`);
  }
  return createResult(true, next, `Successfully updated ${type} position`);
};

export const deleteElement = (rad, { id, className, type }) => {
  if (!id || !className)
    return createResult(
      false,
      null,
      "Element id and class are required for deletion"
    );
  console.log("DELETE ELEMENT: ", id, className, type);

  try {
    const next = deleteElementById(rad, id, className, type);

    return createResult(
      true,
      next,
      `Successfully deleted element ${id} of class ${className}`
    );
  } catch (error) {
    return createResult(
      false,
      null,
      `Error deleting element: ${error.message}`
    );
  }
};

export const addSubthread = (rad, { parent_id, action, child_id } = {}) => {
  if (!parent_id || !action) {
    return createResult(
      false,
      null,
      "Invalid parameters: parent_id and action are required"
    );
  }

  const next = prepareRad(rad, 1);

  const parentActivity = next.activities.find(
    (activity) => activity.id === parent_id
  );
  if (!parentActivity) {
    return createResult(
      false,
      null,
      `Parent activity with ID '${parent_id}' not found`
    );
  }

  // Validate based on refinement type constraints

  if (!["case", "part", "repeat"].includes(parentActivity.type)) {
    return createResult(
      false,
      null,
      `Cannot add subthread to activity type '${parentActivity.type}'`
    );
  }

  if (parentActivity.type === "repeat") {
    return createResult(
      false,
      null,
      "Cannot add subthread: 'repeat' refinements can only have exactly one subthread"
    );
  }

  const existingSubthreads = next.threads.filter(
    (thread) => thread.parent_id === parent_id
  );

  // Check for maximum reasonable subthreads (optional safety limit)
  const MAX_SUBTHREADS = 20; // Reasonable limit to prevent UI issues
  if (existingSubthreads.length >= MAX_SUBTHREADS) {
    return createResult(
      false,
      null,
      `Cannot add subthread: maximum of ${MAX_SUBTHREADS} subthreads allowed per refinement`
    );
  }

  let idx;
  let actionDescription = "";

  switch (action) {
    case "insertAtStart":
      idx = 0;
      actionDescription = "at the beginning";
      break;
    case "addAtEnd":
      idx = Infinity;
      actionDescription = "at the end";
      break;
    case "insertAfter": {
      if (!child_id) {
        return createResult(
          false,
          null,
          "Child ID is required for insertAfter action"
        );
      }
      const childThread = next.threads.find((thread) => thread.id === child_id);
      if (!childThread) {
        return createResult(
          false,
          null,
          `Child thread with ID '${child_id}' not found`
        );
      }
      if (childThread.parent_id !== parent_id) {
        return createResult(
          false,
          null,
          `Child thread '${child_id}' does not belong to parent '${parent_id}'`
        );
      }
      idx = childThread.index + 1;
      actionDescription = `after thread '${childThread.label}'`;
      break;
    }
    default:
      return createResult(
        false,
        null,
        `Unknown action '${action}'. Valid actions: insertAtStart, addAtEnd, insertAfter`
      );
  }

  const { type: parentType } = parentActivity;
  const labelType = parentType === "case" ? "c" : "p";
  const subthreadType = parentType === "case" ? "condition" : "partThread";

  // Generate next available label number
  const existingLabels = existingSubthreads
    .map((t) => t.label)
    .filter((label) => label && label.startsWith(labelType));
  const existingNumbers = existingLabels
    .map((label) => parseInt(label.substring(1)))
    .filter((num) => !isNaN(num));
  const nextNumber =
    existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;

  const id = nanoid();
  const newSubthread = {
    id,
    class: parentType === "case" ? "case" : "part",
    type: subthreadType,
    label: `${labelType}${nextNumber}`,
    parent_id,
    prestate_id: null,
    poststate_id: null,
    index: idx,
    status: "main",
  };

  const subthreads = existingSubthreads.sort(
    (a, b) => (a.index ?? 0) - (b.index ?? 0)
  );
  const index = idx === Infinity ? subthreads.length : idx;

  const newSubthreads = insertAtIndex(subthreads, newSubthread, index);

  next.threads = next.threads
    .filter((thread) => thread.parent_id !== parent_id)
    .concat(newSubthreads.map((thread, index) => ({ ...thread, index })));

  const successMessage = `Successfully added ${subthreadType} '${newSubthread.label}' ${actionDescription}`;
  return createResult(true, next, successMessage);
};

export const moveSubthread = (rad, { id, direction } = {}) => {
  function arrayMove(arr, fromIndex, toIndex) {
    const newArr = arr.slice();
    const [element] = newArr.splice(fromIndex, 1);
    newArr.splice(toIndex, 0, element);
    return newArr;
  }

  //guards
  if (!id) {
    return createResult(false, null, "moveSubthread invalid subthread id");
  }
  if (!["left", "right"].includes(direction)) {
    return createResult(false, null, "moveSubthread invalid direction");
  }
  const subthread = (rad?.threads || []).find((t) => t.id === id);
  if (!subthread) {
    return createResult(false, null, `moveSubthread subthread ${id} not found`);
  }

  // logic
  const next = prepareRad(rad, 1);
  const fromIndex = subthread.index;
  const toIndex = direction === "left" ? fromIndex - 1 : fromIndex + 1;
  if (toIndex < 0) {
    return createResult(false, null, "moveSubthread cannot move further left");
  }
  const siblingThreads = next.threads.filter(
    (t) => t.parent_id === subthread.parent_id
  );
  if (toIndex >= siblingThreads.length) {
    return createResult(false, null, "moveSubthread cannot move further right");
  }
  const reordered = arrayMove(siblingThreads, fromIndex, toIndex).map(
    (t, index) => ({
      ...t,
      index,
    })
  );

  //replace the reordered siblings in next.threads
  next.threads = next.threads
    .filter((t) => t.parent_id !== subthread.parent_id)
    .concat(reordered);

  return createResult(
    true,
    next,
    `Successfully moved subthread '${subthread.label}' ${direction}`
  );
};

export const detachSubthread = (rad, { id } = {}) => {
  if (!id) {
    return createResult(false, null, "subthread id required");
  }
  const subthread = (rad?.threads || []).find((t) => t.id === id);
  if (!subthread) {
    return createResult(false, null, `subthread ${id} not found`);
  }
  const newStateId = nanoid();
  const next = prepareRad(rad, 1);
  ensureState(next, newStateId, "Detached State");
  next.threads = next.threads.map((thread) =>
    thread.id === subthread.id
      ? { ...thread, poststate_id: newStateId, status: "detached" }
      : thread
  );
  // get the last activity in the subthread and set its poststate to the new state
  // this might not be necessary
  const subthreadActivities = getThreadSequenceQuery(next)(subthread.id);
  console.log("SUBTHREAD ACTIVITIES: ", subthreadActivities);
  if (subthreadActivities.length > 0) {
    const lastActivity = subthreadActivities.at(-1); // last activity

    next.activities = next.activities.map((activity) =>
      activity.id === lastActivity.id
        ? { ...activity, poststate_id: newStateId }
        : activity
    );
  }
  return createResult(
    true,
    next,
    `Successfully detached subthread '${subthread.label}'`
  );
};

export const attachSubthread = (rad, { id } = {}) => {
  if (!id) {
    return createResult(false, null, "subthread id required");
  }
  const subthread = (rad?.threads || []).find((t) => t.id === id);
  if (!subthread) {
    return createResult(false, null, `subthread ${id} not found`);
  }
  const next = prepareRad(rad, 1);
  next.threads = next.threads.map((thread) =>
    thread.id === subthread.id
      ? { ...thread, poststate_id: null, status: "attached" }
      : thread
  );
  // get the last activity in the subthread and set its poststate to null
  const subthreadActivities = getThreadSequenceQuery(next)(subthread.id);
  if (subthreadActivities.length > 0) {
    const lastActivity = subthreadActivities.at(-1); // last activity

    next.activities = next.activities.map((activity) =>
      activity.id === lastActivity.id
        ? { ...activity, poststate_id: null }
        : activity
    );
  }
  return createResult(
    true,
    next,
    `Successfully detached subthread '${subthread.label}'`
  );
};

export const mergeStates = (rad, { source_state_id, target_state_id } = {}) => {
  // Merge two states by updating all threads that end at source_state_id
  // to end at target_state_id instead.
  if (!source_state_id || !target_state_id) {
    return createResult(
      false,
      null,
      "source_state_id and target_state_id are required"
    );
  }
  const next = prepareRad(rad, 1);
  const sourceState = next.states.find((state) => state.id === source_state_id);
  const targetState = next.states.find((state) => state.id === target_state_id);
  if (!sourceState) {
    return createResult(
      false,
      null,
      `source state ${source_state_id} not found`
    );
  }
  if (!targetState) {
    return createResult(
      false,
      null,
      `target state ${target_state_id} not found`
    );
  }
  // Get the thread that has a terminal state matching source_state_id
  // and update its poststate to target_state_id
  next.threads = next.threads.map((thread) =>
    thread.poststate_id === source_state_id
      ? {
          ...thread,
          poststate_id: target_state_id,
          status: "merged",
          showLoop: true,
        }
      : thread
  );
  // Also update the activity that has poststate_id matching source_state_id
  next.activities = next.activities.map((activity) =>
    activity.poststate_id === source_state_id
      ? { ...activity, poststate_id: target_state_id }
      : activity
  );
  return createResult(
    true,
    next,
    `Successfully merged states '${sourceState.label}' and '${targetState.label}'`
  );
};

export const unmergeThread = (rad, { thread_id } = {}) => {
  // Given a thread that was merged, restore its poststate to a new state.
  if (!thread_id) {
    return createResult(false, null, "thread_id is required");
  }
  const next = prepareRad(rad, 1);
  const thread = next.threads.find((t) => t.id === thread_id);
  if (!thread) {
    return createResult(false, null, `thread ${thread_id} not found`);
  }
  if (thread.status !== "merged") {
    return createResult(
      false,
      null,
      `thread ${thread_id} is not merged, cannot unmerge`
    );
  }
  const oldStateId = String(thread.poststate_id); //
  const newStateId = nanoid();
  ensureState(next, newStateId, "Unmerged State");

  // Terminal activty has to match the thread's poststate
  next.activities = next.activities.map((activity) =>
    activity.poststate_id === oldStateId && activity.thread_id === thread_id
      ? { ...activity, poststate_id: newStateId }
      : activity
  );

  // Update the thread's poststate to the new state
  next.threads = next.threads.map((t) =>
    t.id === thread_id
      ? { ...t, poststate_id: newStateId, status: "unmerged" }
      : t
  );

  return createResult(
    true,
    next,
    `Successfully unmerged thread '${thread.label}'`
  );
};

export const loopSubthread = (rad, { subthread_id, target_state_id } = {}) => {
  if (!subthread_id || !target_state_id) {
    return createResult(
      false,
      null,
      "subthread_id and target_state_id are required"
    );
  }
  const next = prepareRad(rad, 1);
  const subthread = next.threads.find((t) => t.id === subthread_id);
  if (!subthread) {
    return createResult(false, null, `subthread ${subthread_id} not found`);
  }
  // There must be at least one subthread that returns to the parent acivity.
  const subthreads = next.threads.filter(
    (thread) => thread.parent_id === subthread.parent_id
  );
  if (subthreads.length < 2) {
    // raise error
    return createResult(
      false,
      null,
      "Cannot loop: refinements must have at least one subthread that returns to the parent activity."
    );
  }

  // Ensure target_state_id exists
  const targetState = next.states.find((state) => state.id === target_state_id);
  if (!targetState) {
    return createResult(
      false,
      null,
      `Target state ${target_state_id} not found`
    );
  }

  // set the subthread's poststate to the target_state_id
  next.threads = next.threads.map((thread) =>
    thread.id === subthread.id
      ? {
          ...thread,
          poststate_id: target_state_id,
          status: "looped",
          showLoop: true,
        }
      : thread
  );
  // get the last activity in the subthread and set its poststate to the target state
  const subthreadActivities = getThreadSequenceQuery(next)(subthread.id);
  console.log("SUBTHREAD ACTIVITIES: ", subthreadActivities);
  if (subthreadActivities.length > 0) {
    const lastActivity = subthreadActivities.at(-1); // last activity

    next.activities = next.activities.map((activity) =>
      activity.id === lastActivity.id
        ? { ...activity, poststate_id: target_state_id }
        : activity
    );
  }
  return createResult(
    true,
    next,
    `Successfully looped subthread '${subthread.label}' to state '${targetState.label}'`
  );
};

export const deleteActivity = (rad, activityId) => {
  if (!activityId) return rad;
  const next = deleteElementById(rad, activityId, "activity");
  return next;
};

export const deleteThread = (rad, threadId) => {
  if (!threadId) return rad;
  const thread = (rad?.threads || []).find((t) => t.id === threadId);
  const className = thread?.class ?? "thread";
  const next = deleteElementById(rad, threadId, className);
  return next;
};

/* Can be removed after cli is updated to use deleteElement */
export const deleteEntity = (rad, { id, type } = {}) => {
  if (!id || !type) throw new Error("deleteEntity invalid payload");
  switch (type) {
    case "role":
      return deleteRole(rad, id);
    case "thread":
    case "case":
    case "part":
    case "repeat":
      return deleteThread(rad, id);
    case "activity":
    case "refinement":
      return deleteActivity(rad, id);
    case "connection":
      return deleteElement(rad, { id, className: "connection" });
    default:
      throw new Error(`unsupported delete type ${type}`);
  }
};

const findConnectionByInteractions = (connections, interactions) =>
  connections.find((connection) =>
    connection.interactions.some((id) => interactions.includes(id))
  );

export const addInteraction = (rad, payload = {}) => {
  /*
  The user has asked to add a new interaction between two elements:
  - source: This can be a role, a state, an existing interaction, 
    a refinement thread,
    or an interaction line
    (e.g., the line between two existing interactions)
  - selected: as source, plus loops.

  The rules for adding an interaction are:
  1. If an interaction already exists between the two elements, do not add a new one.
  2. If the selected element is not a valid target for the source element, do not add the interaction.
  3. Otherwise, create a new interaction connecting the two elements.

  The rules for valid targets are defined in getTargetConnectionsQuery.
  */
  const { source, selected } = payload;
  if (!source || !selected) {
    return createResult(false, null, "Source and target elements are required");
  }

  if (source.id === selected.id) {
    return createResult(
      false,
      null,
      "Cannot create interaction between the same element"
    );
  }
  //const next = prepareRad(rad, 1);

  let sourceInteraction = {};
  let next;
  switch (source.class) {
    case "role": {
      // If the source is a role, we need to create a new interaction activity.
      // As this is a new interaction, we can just add it to the role directly.
      console.log("SOURCE IS A ROLE: ", source.id);
      const role = rad.roles.find((r) => r.id === source.id);
      if (!role) {
        return createResult(false, null, `Role ${source.id} not found`);
      }
      // Automatically adding the interaction this way means we need to find
      // the newly created activity by comparing before and after.
      const beforeActivities = new Set((rad.activities || []).map((a) => a.id));
      try {
        next = addActivityToRole(rad, {
          role_id: source.id,
          className: "activity",
          type: "interaction",
          label: "New Interaction",
          //       position: { x: role.size.width - 50, y: role.size.height - 100 },
        });
      } catch (error) {
        return createResult(
          false,
          null,
          `Error adding source interaction activity: ${error.message}`
        );
      }
      // Find the newly added activity by comparing before and after
      sourceInteraction = next.activities.find(
        (a) => !beforeActivities.has(a.id)
      );
      console.log("NEW SOURCE INTERACTION FROM ROLE: ", sourceInteraction);
      break;
    }
    case "state": {
      console.log("SOURCE IS A STATE: ", source.id);
      const thread = getThreadByStateQuery(rad)(source.id);
      if (!thread) {
        return createResult(
          false,
          null,
          `No thread found for source state ${source.id}`
        );
      }
      // If the source is a state then we add a new interaction element
      const beforeActivities = new Set((rad.activities || []).map((a) => a.id));
      try {
        next = addActivityToState(rad, {
          thread_id: thread.id,
          state_id: source.id,
          className: "activity",
          type: "interaction",
          label: "New Interaction",
        });
        // Find the newly added activity by comparing before and after
        sourceInteraction = next.activities.find(
          (a) => !beforeActivities.has(a.id)
        );
      } catch (error) {
        return createResult(
          false,
          null,
          `Error adding interaction activity: ${error.message}`
        );
      }
      break;
    }
    case "connection": {
      console.log("SOURCE IS A CONNECTION: ", source.id);
      // If the source is an existing interaction connection, we just need to get
      // the interactions from it and use the first one (any one would do) as the source interaction
      const connection = rad.connections.find((con) => con.id === source.id);
      if (!connection) {
        return createResult(false, null, `Connection ${source.id} not found`);
      }
      const interactionId = connection.interactions[0];
      sourceInteraction = rad.activities.find(
        (activity) => activity.id === interactionId
      );
      console.log("SOURCE INTERACTION FROM CONNECTION: ", sourceInteraction);
      break;
    }
    case "activity": {
      if (source.type !== "interaction") {
        return createResult(
          false,
          null,
          `Source activity ${source.id} is not an interaction`
        );
      }
      // If the source is an existing interaction activity, we can use it directly
      const activity = rad.activities.find(
        (activity) => activity.id === source.id
      );
      sourceInteraction = activity;
      break;
    }
    case "case":
    case "part":
    case "repeat": {
      console.log("SOURCE IS A REFINEMENT THREAD: ", source.id);
      // If the source is a refinement thread, we need to create a new interaction activity.
      // We add the interaction to the start of the refinement thread.
      const beforeActivities = new Set((rad.activities || []).map((a) => a.id));
      try {
        next = addActivityToSubthread(rad, {
          thread_id: source.id,
          className: "activity",
          type: "interaction",
          label: "New Interaction",
        });
        // Find the newly added activity by comparing before and after
        // Need to check for type interaction to avoid finding void activities
        // that are added to maintain bipartite structure in empty refinement threads
        sourceInteraction = next.activities.find(
          (a) => !beforeActivities.has(a.id) && a.type === "interaction"
        );
      } catch (error) {
        return createResult(
          false,
          null,
          `Error adding interaction activity: ${error.message}`
        );
      }

      break;
    }
    default:
      return createResult(
        false,
        null,
        `Unsupported source type: ${source.type}`
      );
  }
  if (!sourceInteraction || !sourceInteraction.id) {
    return createResult(
      false,
      null,
      `Could not determine source interaction from source element ${source.id}`
    );
  }
  if (!next) {
    next = prepareRad(rad, 1);
  }
  const exists = getConnectionsQuery(next).some(
    (con) =>
      con.interactions.includes(source.id) &&
      con.interactions.includes(selected.id)
  );

  if (exists) {
    return createResult(
      false,
      null,
      `Connection between ${source.id} and ${selected.id} already exists.`
    );
  }
  console.log("CHECKING TARGETS FOR SOURCE INTERACTION: ", next, source);
  // The target really should have been validated at the UI level already
  // but we double check here as we dont want to have to trust the client.
  // It is possible that this could be called from a CLI or other non-UI client.
  const targets = getTargetConnectionsQuery(next)(source);
  if (!targets) {
    console.log("NO TARGETS FOUND FOR SOURCE INTERACTION: ", source);
    return createResult(
      false,
      null,
      `No available connection targets for source ${source.class} ${
        source.label || ""
      }.`
    );
  }
  if (!targets.includes(selected.id)) {
    return createResult(
      false,
      null,
      `Connection target ${selected.id} is not valid for source ${source.id}.`
    );
  }

  // At this point we are clear to add the connection
  // But as with the source we may need to create a new interaction activity
  // if the selected is a role, state or refinement thread.
  let targetInteraction = {};
  switch (selected.class) {
    case "role": {
      console.log("SELECTED TARGET IS A ROLE: ", selected.id);
      // If the selected is a role, we need to create a new interaction activity.
      // As this is a new interaction, we can just add it to the role directly.
      const beforeActivities = new Set(
        (next.activities || []).map((a) => a.id)
      );
      try {
        next = addActivityToRole(next, {
          role_id: selected.id,
          className: "activity",
          type: "interaction",
          label: "New Interaction",
          //     position: DEFAULT_THREAD_POSITION,
        });
        // Find the newly added activity by comparing before and after
        targetInteraction = next.activities.find(
          (a) => !beforeActivities.has(a.id)
        );
        console.log("NEW TARGET INTERACTION FROM ROLE: ", targetInteraction);
      } catch (error) {
        return createResult(
          false,
          null,
          `Error adding source interaction activity: ${error.message}`
        );
      }

      break;
    }
    case "state": {
      console.log("SELECTED TARGET IS A STATE: ", selected.id);
      const thread = getThreadByStateQuery(next)(selected.id);
      if (!thread) {
        return createResult(
          false,
          null,
          `No thread found for selected state ${selected.id}`
        );
      }
      // If the selected is a state then we add a new interaction element
      const beforeActivities = new Set(
        (next.activities || []).map((a) => a.id)
      );
      try {
        next = addActivityToState(next, {
          thread_id: thread.id,
          state_id: selected.id,
          className: "activity",
          type: "interaction",
          label: "New Interaction",
        });
        // Find the newly added activity by comparing before and after
        targetInteraction = next.activities.find(
          (a) => !beforeActivities.has(a.id)
        );
      } catch (error) {
        return createResult(
          false,
          null,
          `Error adding interaction activity: ${error.message}`
        );
      }
      break;
    }
    case "connection": {
      console.log("SELECTED TARGET IS A CONNECTION: ", selected.id);
      // If the selected is an existing interaction connection, we just need to get
      // the interactions from it and use the first one (any one would do) as the target interaction
      targetInteraction = next.connections.find((c) => c.id === selected.id)
        ?.interactions?.[0];
      if (!targetInteraction) {
        return createResult(false, null, `Connection ${selected.id} not found`);
      }
      targetInteraction = next.activities.find(
        (activity) => activity.id === targetInteraction
      );
      console.log("TARGET INTERACTION FROM CONNECTION: ", targetInteraction);
      break;
    }
    case "activity": {
      if (selected.type !== "interaction") {
        return createResult(
          false,
          null,
          `Selected activity ${selected.id} is not an interaction`
        );
      }
      targetInteraction = selected;
      break;
    }
    case "case":
    case "part":
    case "repeat": {
      console.log("SELECTED TARGET IS A REFINEMENT THREAD: ", selected.id);
      // If the selected is a refinement thread, we need to create a new interaction activity.
      // We add the interaction to the start of the refinement thread.
      const beforeActivities = new Set(
        (next.activities || []).map((a) => a.id)
      );
      try {
        next = addActivityToSubthread(next, {
          thread_id: selected.id,
          className: "activity",
          type: "interaction",
          label: "New Interaction",
        });
        // Find the newly added activity by comparing before and after
        // Need to check for type interaction to avoid finding void activities
        // that are added to maintain bipartite structure in empty refinement threads
        targetInteraction = next.activities.find(
          (a) => !beforeActivities.has(a.id) && a.type === "interaction"
        );
      } catch (error) {
        return createResult(
          false,
          null,
          `Error adding interaction activity: ${error.message}`
        );
      }
      break;
    }
    default:
      return createResult(
        false,
        null,
        `Unsupported target type: ${selected.type}`
      );
  }

  try {
    console.log(
      "ADDING CONNECTION BETWEEN: ",
      sourceInteraction,
      targetInteraction
    );
    next = addConnection(next, {
      source: sourceInteraction,
      selected: targetInteraction,
    });
  } catch (error) {
    return createResult(
      false,
      null,
      `Error adding connection: ${error.message}`
    );
  }
  return createResult(
    true,
    next,
    `Successfully added connection between ${source.id} and ${selected.id}`
  );
};

const addConnection = (rad, { source, selected }) => {
  // If we reach here, we can add the connection between source and selected
  const interactions = [source.id, selected.id];

  if (!Array.isArray(interactions) || interactions.length < 2) {
    throw new Error("connection interactions invalid");
  }

  // An interaction can span multiple elements, so we need to check if any of the
  // interactions already exist in a connection. If so, we merge the interactions
  // into that connection instead of creating a new one.

  const uniqueInteractions = ensureUniqueInteractions(interactions);
  const existing = findConnectionByInteractions(
    rad.connections,
    uniqueInteractions
  );
  if (existing) {
    rad.connections = rad.connections.map((connection) =>
      connection.id === existing.id
        ? {
            ...connection,
            interactions: ensureUniqueInteractions(
              connection.interactions.concat(uniqueInteractions)
            ),
          }
        : connection
    );
  } else {
    rad.connections.push({
      id: nanoid(),
      class: "connection",
      interactions: uniqueInteractions,
    });
  }
  return rad;
};

export const deleteInteraction = (rad, { component } = {}) => {
  if (!component?.id) {
    return createResult(false, null, "interaction id is required");
  }
  // Assess the component type and delete accordingly.
  const { class: className, type } = component;
  if (!(className === "connection" || type === "interaction")) {
    return createResult(
      false,
      null,
      `Invalid component type: expected connection interaction, got ${className} ${type}`
    );
  }
  let next = prepareRad(rad, 1);
  if (className === "connection") {
    // Remove the connection from the RAD
    const connection = rad.connections.find((con) => con.id === component.id);
    if (!connection) {
      return createResult(false, null, `connection ${component.id} not found`);
    }
    // Remove the interactions from activities
    connection.interactions.forEach((interactionId) => {
      // Delete correctly
      next = deleteElementById(next, interactionId, "activity");
    });
    next.connections = next.connections.filter(
      (connection) => connection.id !== component.id
    );
  } else {
    // Check for which connection the interaction belongs to and remove it from there
    // If the connection has no more interactions, remove the connection entirely
    const connection = rad.connections.find((con) =>
      con.interactions.includes(component.id)
    );
    if (!connection) {
      console.warn(
        `deleteInteraction: DELETE ORPHAN INTERACTION ${component.id}`
      );
      // This should not happen, but just in case the interaction is orphaned
      next = deleteElementById(next, component.id, "activity");
      return createResult(
        true,
        next,
        `Interaction ${component.id} was orphaned and has been deleted`
      );
    }
    // If the number of interactions is 2, removing one will leave one,
    // which is not a valid connection, so we remove the entire connection
    if (connection.interactions.length === 2) {
      // Remove both interactions from activities
      connection.interactions.forEach((interactionId) => {
        next = deleteElementById(next, interactionId, "activity");
      });
      next.connections = next.connections.filter(
        (con) => con.id !== connection.id
      );
    } else {
      // Just remove the interaction from activities
      next = deleteElementById(next, component.id, "activity");
      // Remove the interaction from the connection
      next.connections = next.connections.map((con) =>
        con.id === connection.id
          ? {
              ...con,
              interactions: con.interactions.filter(
                (id) => id !== component.id
              ),
            }
          : con
      );
    }
  }
  return createResult(
    true,
    next,
    `Successfully deleted connection ${component.id}`
  );
};

export const deleteInteractionSegment = (rad, { id, from, to } = {}) => {
  if (!id || !from || !to) {
    createResult(
      false,
      null,
      "connection id, from, and to interaction ids are required"
    );
  }
  const next = prepareRad(rad, 1);
  const connection = next.connections.find((con) => con.id === id);
  if (!connection) throw new Error(`connection ${id} not found`);
  const interactions = [...connection.interactions];
  const fromIndex = interactions.indexOf(from);
  const toIndex = interactions.indexOf(to);
  if (fromIndex !== -1 && toIndex === fromIndex + 1) {
    interactions.splice(toIndex, 1);
  }
  next.connections = next.connections.map((con) =>
    con.id === id ? { ...con, interactions } : con
  );
  return createResult(
    true,
    next,
    `Successfully deleted interaction segment from ${from} to ${to} in connection ${id}`
  );
};

export const toggleShowLoop = (rad, { id } = {}) => {
  if (!id) {
    return createResult(false, null, "subthread_id is required");
  }
  const next = prepareRad(rad, 1);
  const subthread = next.threads.find((t) => t.id === id);
  if (!subthread) {
    return createResult(false, null, `subthread ${id} not found`);
  }
  next.threads = next.threads.map((thread) =>
    thread.id === subthread.id
      ? {
          ...thread,
          showLoop: !subthread.showLoop,
        }
      : thread
  );
  return createResult(
    true,
    next,
    `Toggled showLoop for subthread '${subthread.label}' to ${!subthread.showLoop}`
  );
};

export const validate = (rad) => {
  if (!rad || typeof rad !== "object") {
    throw new Error("rad must be an object");
  }
  const roles = ensureArray(rad.roles);
  const threads = ensureArray(rad.threads);
  const activities = ensureArray(rad.activities);
  const states = ensureArray(rad.states);
  const connections = ensureArray(rad.connections);

  const roleIds = new Set();
  roles.forEach((role) => {
    if (!role?.id) throw new Error("role missing id");
    if (roleIds.has(role.id)) throw new Error(`duplicate role ${role.id}`);
    roleIds.add(role.id);
  });

  const stateIds = new Set();
  states.forEach((state) => {
    if (!state?.id) throw new Error("state missing id");
    if (stateIds.has(state.id)) throw new Error(`duplicate state ${state.id}`);
    stateIds.add(state.id);
  });

  const threadIds = new Set();
  threads.forEach((thread) => {
    if (!thread?.id) throw new Error("thread missing id");
    if (threadIds.has(thread.id))
      throw new Error(`duplicate thread ${thread.id}`);
    threadIds.add(thread.id);
    if (thread.role_id && !roleIds.has(thread.role_id)) {
      throw new Error(
        `thread ${thread.id} references missing role ${thread.role_id}`
      );
    }
    if (thread.prestate_id && !stateIds.has(thread.prestate_id)) {
      throw new Error(
        `thread ${thread.id} missing prestate ${thread.prestate_id}`
      );
    }
    if (thread.poststate_id && !stateIds.has(thread.poststate_id)) {
      throw new Error(
        `thread ${thread.id} missing poststate ${thread.poststate_id}`
      );
    }
    if (
      thread.parent_id &&
      !activities.some((a) => a.id === thread.parent_id)
    ) {
      throw new Error(
        `thread ${thread.id} missing parent refinement ${thread.parent_id}`
      );
    }
  });

  const activityIds = new Set();
  activities.forEach((activity) => {
    if (!activity?.id) throw new Error("activity missing id");
    if (activityIds.has(activity.id)) {
      throw new Error(`duplicate activity ${activity.id}`);
    }
    activityIds.add(activity.id);
    if (!activity.thread_id || !threadIds.has(activity.thread_id)) {
      throw new Error(
        `activity ${activity.id} missing thread ${activity.thread_id}`
      );
    }
    if (activity.prestate_id && !stateIds.has(activity.prestate_id)) {
      throw new Error(
        `activity ${activity.id} missing prestate ${activity.prestate_id}`
      );
    }
    if (activity.poststate_id && !stateIds.has(activity.poststate_id)) {
      throw new Error(
        `activity ${activity.id} missing poststate ${activity.poststate_id}`
      );
    }
  });

  connections.forEach((connection) => {
    if (!connection?.id) throw new Error("connection missing id");
    if (
      !Array.isArray(connection.interactions) ||
      connection.interactions.length < 2
    ) {
      throw new Error(
        `connection ${connection.id} must link at least two activities`
      );
    }
    connection.interactions.forEach((actId) => {
      if (!activityIds.has(actId)) {
        throw new Error(
          `connection ${connection.id} references missing activity ${actId}`
        );
      }
    });
  });

  return true;
};

export const withValidation = (op) => (rad, payload) => {
  const result = op(rad, payload);
  validate(result);
  return result;
};

export const guards = {
  addRole: (rad, payload) => withValidation(addRole)(rad, payload),
  updateRole: (rad, payload) => withValidation(updateRole)(rad, payload),
  deleteRole: (rad, roleId) => withValidation(deleteRole)(rad, roleId),
  addActivity: (rad, payload) => withValidation(addActivity)(rad, payload),
  moveSubthread: (rad, payload) => withValidation(moveSubthread)(rad, payload),
};
